export const AI_DASHBOARD_ADDON_DEFINITIONS = [
  {
    key: 'ai_assistant',
    label: 'AI Assistant',
    description: 'Multi-provider conversational AI assistant — branded chat, multiple agents, usage analytics and licensing. Purchasable as a paid add-on for any subscription or tenant.',
    tabId: 'ai-assistant-chat',
    defaultPricing: {
      monthly_price: 149,
      yearly_price: 1490,
    },
  },
  {
    key: 'ai_attendance_insight',
    label: 'AI Attendance Insight',
    description: 'Attendance scoring, attendance-risk visibility, and workforce activity signals.',
    tabId: 'ai-center/attendance-insight',
    defaultPricing: {
      monthly_price: 1499,
      yearly_price: 14999,
    },
  },
  {
    key: 'ai_hiring_forecast',
    label: 'AI Hiring Forecast',
    description: 'Hiring demand modeling, candidate quality signals, and funnel bottleneck forecasting.',
    tabId: 'ai-center/hiring-forecast',
    defaultPricing: {
      monthly_price: 1999,
      yearly_price: 19999,
    },
  },
  {
    key: 'ai_payroll_forecast',
    label: 'AI Payroll Forecast',
    description: 'Payroll cost forecasting, anomaly visibility, and compensation pressure analysis.',
    tabId: 'ai-center/payroll-forecast',
    defaultPricing: {
      monthly_price: 2499,
      yearly_price: 24999,
    },
  },
  {
    key: 'ai_team_performance',
    label: 'AI Team Performance',
    description: 'Team productivity, burnout risk, engagement trends, and skill-gap visibility.',
    tabId: 'ai-center/team-performance',
    defaultPricing: {
      monthly_price: 1799,
      yearly_price: 17999,
    },
  },
  {
    key: 'ai_settings',
    label: 'AI Settings',
    description: 'AI model controls, training configuration, permissions, and capability governance.',
    tabId: 'ai-center/settings',
    defaultPricing: {
      monthly_price: 999,
      yearly_price: 9999,
    },
  },
];

// Education Suite modules, sold à la carte. Each key is BOTH the paid add-on key and
// the module gate — enabling one grants exactly that module (the backend adds the
// matching edu_* module_access key). No blanket "unlock everything" toggle.
export const SUITE_ADDON_DEFINITIONS = [
  {
    key: 'edu_faculty',
    suite: 'education',
    label: 'Education · Faculty Management',
    description: 'Faculty profiles that extend employees — academic rank, faculty type, specialization, workload, committees and mentor groups.',
    tabId: 'edu-faculty',
    defaultPricing: { monthly_price: 1499, yearly_price: 14999 },
  },
  {
    key: 'edu_calendar',
    suite: 'education',
    label: 'Education · Academic Calendar',
    description: 'Academic years, semesters/terms, and academic events (exams, holidays, admissions) with teaching-blocked dates.',
    tabId: 'edu-calendar',
    defaultPricing: { monthly_price: 799, yearly_price: 7999 },
  },
  {
    key: 'edu_timetable',
    suite: 'education',
    label: 'Education · Timetable & Workload',
    description: 'Courses, rooms and weekly slots with DB-level conflict detection and substitutions, plus per-faculty workload aggregation against configurable norms.',
    tabId: 'edu-timetable',
    defaultPricing: { monthly_price: 1499, yearly_price: 14999 },
  },
  {
    key: 'edu_contracts',
    suite: 'education',
    label: 'Education · Faculty Contracts',
    description: 'Semester / academic-year / visiting / adjunct contracts with expiry dashboards and renewal workflow.',
    tabId: 'edu-contracts',
    defaultPricing: { monthly_price: 999, yearly_price: 9999 },
  },
  {
    key: 'edu_accreditation',
    suite: 'education',
    label: 'Education · Accreditation',
    description: 'NAAC / NBA / AICTE / UGC / NIRF / ISO frameworks with weighted readiness scoring and exports.',
    tabId: 'edu-accreditation',
    defaultPricing: { monthly_price: 1999, yearly_price: 19999 },
  },
  {
    key: 'edu_research',
    suite: 'education',
    label: 'Education · Research & Publications',
    description: 'Publications, patents, grants and researcher IDs (ORCID/Scopus) with annual research reports.',
    tabId: 'edu-research',
    defaultPricing: { monthly_price: 1199, yearly_price: 11999 },
  },
  {
    key: 'edu_grievance',
    suite: 'education',
    label: 'Education · Grievance Management',
    description: 'Anonymous, SLA-tracked, multi-level grievance workflow (POSH, academic, ethics, whistleblower).',
    tabId: 'edu-grievance',
    defaultPricing: { monthly_price: 899, yearly_price: 8999 },
  },
  {
    key: 'edu_student_worker',
    suite: 'education',
    label: 'Education · Student Workers',
    description: 'Teaching/research assistants, interns and campus ambassadors with assignments and hour caps.',
    tabId: 'edu-student-workers',
    defaultPricing: { monthly_price: 699, yearly_price: 6999 },
  },
  {
    key: 'edu_adjunct',
    suite: 'education',
    label: 'Education · Adjunct Faculty',
    description: 'Course-wise adjunct appointments with hourly compensation and renewal.',
    tabId: 'edu-adjunct',
    defaultPricing: { monthly_price: 699, yearly_price: 6999 },
  },
  {
    key: 'edu_integrations',
    suite: 'education',
    label: 'Education · Integrations',
    description: 'Connectors for LMS (Moodle/Canvas/Classroom), SIS, M365/Google Workspace and SSO (SAML/OAuth).',
    tabId: 'edu-integrations',
    defaultPricing: { monthly_price: 1499, yearly_price: 14999 },
  },

// ── Per-vertical add-ons ───────────────────────────────────────────────────
//
// Every vertical except Education was offered nothing but the cross-vertical AI
// add-ons, so an operator opening a Hospitality tenant saw five AI toggles and
// nothing about rosters, properties or geofencing — the things that workspace
// actually buys.
//
// Each key here is a real module from plan_access.MODULE_CATALOG, and holding
// the add-on grants exactly that module (see ADDON_MODULE_SUITE server-side).
// An add-on key with no module behind it would be a toggle that changes
// nothing, which is worse than not offering it.
//
// `free: true` means included at no charge on any plan that carries the
// vertical — offered so the value is visible and switchable, not to be billed.

  // Hospitality — rostered, on-premises, multi-property.
  {
    key: 'geofence',
    suite: 'hospitality',
    label: 'Hospitality · Geofenced Attendance',
    description: 'Punch-in restricted to the property perimeter, with per-site radius and drift alerts.',
    tabId: 'geofence',
    defaultPricing: { monthly_price: 999, yearly_price: 9999 },
  },
  {
    key: 'multi_entity',
    suite: 'hospitality',
    label: 'Hospitality · Multi-Property',
    description: 'Each property as its own legal entity and business unit, with headcount and payroll rolled up.',
    tabId: 'org-legal-entities',
    defaultPricing: { monthly_price: 1299, yearly_price: 12999 },
  },
  {
    key: 'gamification',
    suite: 'hospitality',
    free: true,
    label: 'Hospitality · Recognition',
    description: 'Shift-level recognition and rewards, included with the vertical.',
    tabId: 'engagement-rewards',
    defaultPricing: { monthly_price: 0, yearly_price: 0 },
  },

  // Automobile — dealerships, workshops, issued tooling.
  {
    key: 'assets',
    suite: 'automobile',
    label: 'Automobile · Tool & Asset Tracking',
    description: 'Tools and equipment issued to technicians, with custody, return and write-off history.',
    tabId: 'assets',
    defaultPricing: { monthly_price: 899, yearly_price: 8999 },
  },
  {
    key: 'multi_entity',
    suite: 'automobile',
    label: 'Automobile · Multi-Dealership',
    description: 'Dealerships and workshops as separate entities, consolidated for reporting.',
    tabId: 'org-legal-entities',
    defaultPricing: { monthly_price: 1299, yearly_price: 12999 },
  },
  {
    key: 'gamification',
    suite: 'automobile',
    free: true,
    label: 'Automobile · Service Targets',
    description: 'Technician targets, leaderboards and recognition. Included with the vertical.',
    tabId: 'engagement-rewards',
    defaultPricing: { monthly_price: 0, yearly_price: 0 },
  },

  // Industry — shop floor, statutory-heavy, shift rosters.
  {
    key: 'esi',
    suite: 'industry',
    free: true,
    label: 'Industry · ESI & PF Compliance',
    description: 'ESI and PF registers, contributions and challans. Included — a factory cannot run without it.',
    tabId: 'esi-policy',
    defaultPricing: { monthly_price: 0, yearly_price: 0 },
  },
  {
    key: 'geofence',
    suite: 'industry',
    label: 'Industry · Plant Geofencing',
    description: 'Attendance bound to the plant perimeter, with gate-level radius control.',
    tabId: 'geofence',
    defaultPricing: { monthly_price: 999, yearly_price: 9999 },
  },
  {
    key: 'fraud',
    suite: 'industry',
    label: 'Industry · Attendance Integrity',
    description: 'Buddy-punching detection, liveness signals and shift-fraud flags.',
    tabId: 'fraud',
    defaultPricing: { monthly_price: 1499, yearly_price: 14999 },
  },

  // Medical — rostered clinical staff, credentials, multi-site.
  {
    key: 'multi_entity',
    suite: 'medical',
    label: 'Medical · Multi-Facility',
    description: 'Hospitals, clinics and labs as separate entities with consolidated workforce reporting.',
    tabId: 'org-legal-entities',
    defaultPricing: { monthly_price: 1499, yearly_price: 14999 },
  },
  {
    key: 'geofence',
    suite: 'medical',
    label: 'Medical · On-Site Attendance',
    description: 'Attendance restricted to facility premises, per site.',
    tabId: 'geofence',
    defaultPricing: { monthly_price: 999, yearly_price: 9999 },
  },
  {
    key: 'sso',
    suite: 'medical',
    label: 'Medical · SSO & Provisioning',
    description: 'SAML sign-in and SCIM provisioning against the hospital directory.',
    tabId: 'enterprise-sso',
    defaultPricing: { monthly_price: 1999, yearly_price: 19999 },
  },

  // Corporate — knowledge work: engagement, analytics, identity.
  {
    key: 'pulse_surveys',
    suite: 'corporate',
    free: true,
    label: 'Corporate · Engagement Surveys',
    description: 'Pulse surveys, action plans and trend tracking. Included with the vertical.',
    tabId: 'pulse-surveys',
    defaultPricing: { monthly_price: 0, yearly_price: 0 },
  },
  {
    key: 'executive_analytics',
    suite: 'corporate',
    label: 'Corporate · Executive Analytics',
    description: 'Board-level workforce analytics, export packs and period-over-period snapshots.',
    tabId: 'report-executive',
    defaultPricing: { monthly_price: 2499, yearly_price: 24999 },
  },
  {
    key: 'sso',
    suite: 'corporate',
    label: 'Corporate · SSO & Provisioning',
    description: 'SAML sign-in and SCIM provisioning against the corporate directory.',
    tabId: 'enterprise-sso',
    defaultPricing: { monthly_price: 1999, yearly_price: 19999 },
  },

  // IT / SaaS — equity, developer tooling, identity.
  {
    key: 'equity',
    suite: 'it_saas',
    label: 'IT/SaaS · Equity & Vesting',
    description: 'ESOP and RSU grants, vesting schedules, cliffs and exercise windows.',
    tabId: 'equity',
    defaultPricing: { monthly_price: 1999, yearly_price: 19999 },
  },
  {
    key: 'integrations',
    suite: 'it_saas',
    free: true,
    label: 'IT/SaaS · Dev Tool Connectors',
    description: 'Slack, GitHub, Jira and Linear connectors. Included with the vertical.',
    tabId: 'integrations',
    defaultPricing: { monthly_price: 0, yearly_price: 0 },
  },
  {
    key: 'sso',
    suite: 'it_saas',
    label: 'IT/SaaS · SSO & Provisioning',
    description: 'SAML sign-in and SCIM provisioning.',
    tabId: 'enterprise-sso',
    defaultPricing: { monthly_price: 1999, yearly_price: 19999 },
  },

  // BFSI — identity, audit and controls first.
  {
    key: 'sso',
    suite: 'bfsi',
    free: true,
    label: 'BFSI · SSO & Provisioning',
    description: 'SAML sign-in and SCIM provisioning. Included — regulated tenants cannot run on passwords alone.',
    tabId: 'enterprise-sso',
    defaultPricing: { monthly_price: 0, yearly_price: 0 },
  },
  {
    key: 'fraud',
    suite: 'bfsi',
    label: 'BFSI · Attendance Integrity',
    description: 'Attendance fraud signals and audit-grade evidence trails.',
    tabId: 'fraud',
    defaultPricing: { monthly_price: 1799, yearly_price: 17999 },
  },
  {
    key: 'backup_restore',
    suite: 'bfsi',
    label: 'BFSI · Backup & Restore',
    description: 'Scheduled workspace backups with point-in-time restore, for retention obligations.',
    tabId: 'workspace',
    defaultPricing: { monthly_price: 1499, yearly_price: 14999 },
  },

  // Retail — stores, footfall-driven rosters, recognition.
  {
    key: 'multi_entity',
    suite: 'retail',
    label: 'Retail · Multi-Store',
    description: 'Stores and regions as separate entities with consolidated workforce reporting.',
    tabId: 'org-legal-entities',
    defaultPricing: { monthly_price: 1299, yearly_price: 12999 },
  },
  {
    key: 'geofence',
    suite: 'retail',
    label: 'Retail · Store Attendance',
    description: 'Attendance bound to the store perimeter, per location.',
    tabId: 'geofence',
    defaultPricing: { monthly_price: 999, yearly_price: 9999 },
  },
  {
    key: 'gamification',
    suite: 'retail',
    free: true,
    label: 'Retail · Store Recognition',
    description: 'Store-level targets, leaderboards and rewards. Included with the vertical.',
    tabId: 'engagement-rewards',
    defaultPricing: { monthly_price: 0, yearly_price: 0 },
  },
];

// Combined catalog used by the superadmin plan + tenant editors to render add-on
// toggles. AI-center gating helpers below intentionally stay on the AI-only list.
export const ALL_ADDON_DEFINITIONS = [...AI_DASHBOARD_ADDON_DEFINITIONS, ...SUITE_ADDON_DEFINITIONS];

/** The add-ons worth offering a tenant on this vertical.
 *
 *  Every tenant editor used to render the whole catalog, so an IT/SaaS
 *  workspace was offered ten Education Suite modules — Faculty Management,
 *  Accreditation, Timetables — none of which mean anything to it. The vertical
 *  registry already knows: it lists the modules it hides, and names the
 *  industry suite it belongs to.
 *
 *  Suite-tagged add-ons appear only on their own suite's vertical. Untagged
 *  ones (the AI add-ons) are cross-vertical and always offered. With no bundle
 *  loaded the answer is the full catalog, so a failed lookup shows too much
 *  rather than silently hiding something a tenant is paying for.
 */
export const addonsForVertical = (bundle, definitions = ALL_ADDON_DEFINITIONS) => {
  if (!bundle) return [...definitions];
  const hidden = new Set(bundle.hidden_modules || []);
  const suite = bundle.industry_suite || null;
  return definitions.filter((def) => {
    if (hidden.has(def.key)) return false;
    return def.suite ? def.suite === suite : true;
  });
};

/** A row from `/api/addons/registry`, in the shape this file already speaks.
 *
 *  The server is the catalog now - it is what `plan_access` reads to decide
 *  grants - but it stores `default_pricing: {monthly, yearly}` where this file
 *  has always used `defaultPricing: {monthly_price, yearly_price}`. Translating
 *  here keeps every existing caller working unchanged.
 */
export const normalizeServerAddon = (row) => ({
  key: row.key,
  label: row.label || row.key,
  description: row.description || '',
  suite: row.suite || undefined,
  free: Boolean(row.free),
  tabId: row.tab_id || undefined,
  grantsModule: Boolean(row.grants_module),
  defaultPricing: {
    monthly_price: Number(row.default_pricing?.monthly ?? 0),
    yearly_price: Number(row.default_pricing?.yearly ?? 0),
  },
});

/** The catalog to render, preferring the server's.
 *
 *  Falls back to the compiled-in list when the endpoint is unavailable, and
 *  keeps any compiled-in row the server does not know about, so a console that
 *  cannot reach the API is no worse off than before it could.
 *
 *  This is what makes an add-on a tenant *holds* visible: `white_label` is on
 *  tenant 419 and was in neither catalog, so the editor could not draw it and
 *  therefore could not switch it off.
 */
export const mergeAddonCatalog = (serverRows) => {
  if (!Array.isArray(serverRows) || serverRows.length === 0) return [...ALL_ADDON_DEFINITIONS];
  const fromServer = serverRows.map(normalizeServerAddon);
  const seen = new Set(fromServer.map((d) => d.key));
  return [...fromServer, ...ALL_ADDON_DEFINITIONS.filter((d) => !seen.has(d.key))];
};
// Deduplicated: the same module is sold to more than one vertical — `geofence`
// to Hospitality, Industry, Medical and Retail — so the flat list of keys has
// to collapse them or a plan editor filtering against it counts one add-on four
// times.
export const ALL_ADDON_KEYS = [...new Set(ALL_ADDON_DEFINITIONS.map((addon) => addon.key))];

/** The definitions worth showing for one vertical, keyed for label lookup.
 *
 *  Needed because a bare key is ambiguous now: `geofence` is "Plant Geofencing"
 *  for Industry and "Store Attendance" for Retail, and rendering the wrong one
 *  describes a feature the tenant is not buying.
 */
export const addonLookupForVertical = (bundle) =>
  addonsForVertical(bundle).reduce((acc, addon) => ({ ...acc, [addon.key]: addon }), {});

/** True when this add-on is included rather than billed. */
export const isFreeAddon = (addon) => Boolean(addon?.free);

// True when the tenant is entitled to the AI Assistant add-on — either the
// subscription plan includes it or the tenant has it enabled as an additional
// add-on (both merge into the effective usage-limit `addons`). Superadmin always
// has access. Used to gate the AI Assistant navigation + routes.
export const hasAiAssistantAddon = (user) => {
  if (!user) return false;
  if (user.isSuperadmin) return true;
  const addons = user?.limits?.addons;
  return Array.isArray(addons) && addons.includes('ai_assistant');
};

// A cross-vertical definition wins over a suite-tagged one of the same key, so
// the generic label is the fallback rather than whichever vertical happened to
// be declared last. For a vertical-accurate label use addonLookupForVertical.
export const AI_DASHBOARD_ADDON_LOOKUP = ALL_ADDON_DEFINITIONS.reduce(
  (acc, addon) => ({
    ...acc,
    [addon.key]: acc[addon.key] && !acc[addon.key].suite ? acc[addon.key] : addon,
  }),
  {}
);

const normalizeCurrency = (value) => String(value || 'INR').trim().toUpperCase() || 'INR';

const normalizeAmount = (value, fallback = 0) => {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : fallback;
};

export const getAiDashboardAddonPricing = (limits = {}, addonKey) => {
  const addon = AI_DASHBOARD_ADDON_LOOKUP[addonKey];
  const storedPricing = limits?.addon_pricing?.[addonKey] || {};
  const defaultPricing = addon?.defaultPricing || {};

  return {
    monthly_price: normalizeAmount(storedPricing.monthly_price, normalizeAmount(defaultPricing.monthly_price, 0)),
    yearly_price: normalizeAmount(storedPricing.yearly_price, normalizeAmount(defaultPricing.yearly_price, 0)),
    currency: normalizeCurrency(storedPricing.currency || limits?.currency || limits?.pricing_controls?.currency || 'INR'),
  };
};

export const isAiDashboardAddonEnabled = (limits = {}, addonKey, isSuperadmin = false) => {
  if (isSuperadmin) return true;
  const addons = Array.isArray(limits?.addons) ? limits.addons : [];
  return addons.includes(addonKey);
};

export const listEnabledAiDashboardAddons = (limits = {}, isSuperadmin = false) =>
  AI_DASHBOARD_ADDON_DEFINITIONS.filter((addon) => isAiDashboardAddonEnabled(limits, addon.key, isSuperadmin));

export const getAiDashboardAddonTotals = (limits = {}, isSuperadmin = false) =>
  listEnabledAiDashboardAddons(limits, isSuperadmin).reduce(
    (acc, addon) => {
      const pricing = getAiDashboardAddonPricing(limits, addon.key);
      return {
        monthly_price: acc.monthly_price + Number(pricing.monthly_price || 0),
        yearly_price: acc.yearly_price + Number(pricing.yearly_price || 0),
        currency: pricing.currency || acc.currency || 'INR',
      };
    },
    { monthly_price: 0, yearly_price: 0, currency: normalizeCurrency(limits?.currency || limits?.pricing_controls?.currency || 'INR') }
  );

export const formatAddonPrice = (pricing = {}, cadence = 'monthly') => {
  const currency = normalizeCurrency(pricing.currency);
  const amount = cadence === 'yearly'
    ? normalizeAmount(pricing.yearly_price, 0)
    : normalizeAmount(pricing.monthly_price, 0);

  return new Intl.NumberFormat('en-IN', {
    style: 'currency',
    currency,
    maximumFractionDigits: 0,
  }).format(amount);
};
